import React, { useState, useRef, useEffect } from 'react';
import styles from 'components/show_drawing/measure_tool.module.scss';

// Phase A: an ephemeral on-drawing measure tool for entitled users. When the
// tool is switched on the rest of the dieline dims and a ruler appears with two
// endpoint handles. The measurement is ALWAYS valid: both handles are real
// points on the dieline, they are always axis-aligned (the ruler is a straight
// horizontal or vertical line), and they are always on DIFFERENT edges — so the
// ruler measures ACROSS the shape and never runs along a single edge. The
// orientation is a locked measurement TYPE the user picks (Horizontal or
// Vertical): dragging a handle slides it along the edges and the far handle snaps
// to the real point straight across on that axis, but a drag NEVER changes the
// orientation — only picking a different type does. Nothing is saved — the
// "+ Add to drawing" persistence is a later phase.
//
// The real coordinates already live in the injected SVG: every dieline segment
// is a `<line class="svg-line" x1 y1 x2 y2>` in millimeters (1 SVG user unit =
// 1 mm). svg-pan-zoom wraps the geometry in a transformed group, so screen
// pixels are NOT millimeters at display time; we map between the two through the
// geometry's screen CTM.

const HANDLE_RADIUS_PX = 8;
const GRAB_RADIUS_PX = 26;
const CENTER_SNAP_PX = 16;
const AXIS_LOCK_THRESHOLD_PX = 6;
const COINCIDENT_MM = 0.5;
const PARALLEL_RATIO = 0.05;
const MM_PER_INCH = 25.4;
// The gap (in drawing mm) a witness line stops short of its measured point by, so
// the extension line reads as separate from the geometry. Mirrors the server's
// WITNESS_GAP_IN_MM.
const WITNESS_GAP_MM = 3;
// A circle/arc must be at least this radius (drawing mm) to be measurable — smaller
// curves (rounded corners, tiny fillets) are too cramped to place a radial dimension
// on, so the Radius tool skips them: they're not selectable, not highlighted, and
// never the default shape (nor is Radius offered when every curve is below it).
const MINIMUM_RADIUS_MM = 3;

// The two measurement types the tool offers. Each locks the ruler to one axis so
// dragging can never flip a horizontal measurement into a vertical one (or the
// reverse) — the source of a lot of accidental re-measuring. More types can be
// added here later. A type maps to the axis its ruler runs along: 'horizontal' →
// 'h', 'vertical' → 'v'.
const MEASUREMENT_TYPES = ['horizontal', 'vertical', 'angled', 'radius'];
const axisForType = (type) => (type === 'horizontal' ? 'h' : 'v');

// The last-used type is remembered so repeat measuring doesn't force re-picking.
const STORED_TYPE_KEY = 'measure_tool_type';
const readStoredMeasurementType = () => {
  try {
    const stored = localStorage.getItem(STORED_TYPE_KEY);
    return MEASUREMENT_TYPES.includes(stored) ? stored : 'horizontal';
  } catch (error) { return 'horizontal'; }
};
const storeMeasurementType = (type) => {
  try { localStorage.setItem(STORED_TYPE_KEY, type); } catch (error) { /* storage unavailable */ }
};

const svgRoot = () => document.querySelector('#svg');

// Only the real dieline segments carry a bare `svg-line`; the dimension-text
// and A/B/C helper lines are also `svg-line` but tagged `dmt-line`, so we
// exclude those everywhere (their endpoints are not real dieline points).
const GEOMETRY_LINE_SELECTOR = '#svg .svg-line:not(.dmt-line)';

const geometryMatrix = () => {
  const line = document.querySelector(GEOMETRY_LINE_SELECTOR);
  if (!line || typeof line.getScreenCTM !== 'function') { return null; }
  return line.getScreenCTM();
};

const userToClient = (point, matrix) => {
  const root = svgRoot();
  if (!root || !matrix || !point) { return null; }
  const svgPoint = root.createSVGPoint();
  svgPoint.x = point.x;
  svgPoint.y = point.y;
  return svgPoint.matrixTransform(matrix);
};

const clientToUser = (clientX, clientY, matrix) => {
  const root = svgRoot();
  if (!root || !matrix) { return null; }
  const svgPoint = root.createSVGPoint();
  svgPoint.x = clientX;
  svgPoint.y = clientY;
  return svgPoint.matrixTransform(matrix.inverse());
};

// The real dieline segments, in millimeters.
const gatherSegments = () => {
  const segments = [];
  document.querySelectorAll(GEOMETRY_LINE_SELECTOR).forEach((line) => {
    const ax = parseFloat(line.getAttribute('x1'));
    const ay = parseFloat(line.getAttribute('y1'));
    const bx = parseFloat(line.getAttribute('x2'));
    const by = parseFloat(line.getAttribute('y2'));
    if ([ax, ay, bx, by].some(Number.isNaN)) { return; }
    // `crease` marks the green fold lines (color_code 3) — the Angled type measures
    // the perpendicular gap between them.
    const crease = (line.getAttribute('class') || '').split(/\s+/).includes('color-3');
    segments.push({ ax, ay, bx, by, crease });
  });
  return segments;
};

// Whether two gathered segment sets describe the same dieline geometry — same
// count and identical endpoints in order. A redraw for the same dimensions (e.g.
// adding or removing an annotation) reproduces the geometry exactly, so this
// tells a geometry-changing redraw (new dimensions) apart from an annotation-only
// one.
const sameSegments = (a, b) => {
  if (a.length !== b.length) { return false; }
  return a.every((segment, index) => {
    const other = b[index];
    return segment.ax === other.ax && segment.ay === other.ay
      && segment.bx === other.bx && segment.by === other.by;
  });
};

// Every discrete snap point on the dieline, in millimeters — the fixed targets a
// handle can be dragged straight onto. These are the segment endpoints (the
// corners/nodes where the straight edges meet) together with the circle and arc
// centres: a circle carries its centre as `cx`/`cy`; an arc is a `<path>` with
// the centre emitted as `data-cx`/`data-cy` (y flipped like the rest of the
// geometry). Endpoints shared by adjacent edges are de-duplicated so a corner is
// one point, not one per edge meeting there. Each is a real, formula-driven
// point, so a measurement snapped to it anchors and resizes exactly like an edge
// point.
const gatherSnapPoints = () => {
  const points = [];
  const seen = new Set();
  const add = (x, y) => {
    if ([x, y].some(Number.isNaN)) { return; }
    const key = `${x.toFixed(3)},${y.toFixed(3)}`;
    if (seen.has(key)) { return; }
    seen.add(key);
    points.push({ x, y });
  };
  document.querySelectorAll(GEOMETRY_LINE_SELECTOR).forEach((line) => {
    const x1 = parseFloat(line.getAttribute('x1'));
    const y1 = parseFloat(line.getAttribute('y1'));
    const x2 = parseFloat(line.getAttribute('x2'));
    const y2 = parseFloat(line.getAttribute('y2'));
    add(x1, y1);
    add(x2, y2);
    // The line's centre, too — a snap target that signals where a handle can land
    // (the Angled type anchors at a crease's midpoint).
    add((x1 + x2) / 2, (y1 + y2) / 2);
  });
  document.querySelectorAll('#svg .svg-circle').forEach((circle) => {
    add(parseFloat(circle.getAttribute('cx')), parseFloat(circle.getAttribute('cy')));
  });
  document.querySelectorAll('#svg .svg-arc[data-cx]').forEach((arc) => {
    add(parseFloat(arc.getAttribute('data-cx')), parseFloat(arc.getAttribute('data-cy')));
  });
  return points;
};

// The drawing's circles and arcs as { x, y, r } (centre + radius, in SVG user
// space) — the targets the Radius type measures. Circles carry `r`; arcs carry
// `data-r` (added to the arc SVG so the radius is available client-side).
const RADIUS_CIRCLE_SELECTOR = '#svg .svg-circle';
const RADIUS_ARC_SELECTOR = '#svg .svg-arc[data-r]';
// How many points each circle/arc curve is sampled into. The samples are the click
// targets (you select a shape by clicking ON its curve) and the highlight the tool
// draws over the selectable curves, so they need to be dense enough that a click
// anywhere along the curve lands within grab range of a sample.
const RADIUS_CURVE_SAMPLES = 24;
const gatherRadiusTargets = () => {
  const targets = [];
  document.querySelectorAll(RADIUS_CIRCLE_SELECTOR).forEach((circle) => {
    const x = parseFloat(circle.getAttribute('cx'));
    const y = parseFloat(circle.getAttribute('cy'));
    const r = parseFloat(circle.getAttribute('r'));
    // Skip curves too small to measure — a sub-minimum radius is too cramped to offer
    // as a target (see MINIMUM_RADIUS_MM).
    if ([x, y, r].some(Number.isNaN) || r < MINIMUM_RADIUS_MM) { return; }
    // The circumference, sampled (and closed) so a click ON the circle selects it,
    // like an arc's curve below.
    const curve = [];
    for (let i = 0; i <= RADIUS_CURVE_SAMPLES; i += 1) {
      const theta = (i / RADIUS_CURVE_SAMPLES) * 2 * Math.PI;
      curve.push({ x: x + (r * Math.cos(theta)), y: y + (r * Math.sin(theta)) });
    }
    targets.push({ x, y, r, curve });
  });
  document.querySelectorAll(RADIUS_ARC_SELECTOR).forEach((arc) => {
    const x = parseFloat(arc.getAttribute('data-cx'));
    const y = parseFloat(arc.getAttribute('data-cy'));
    const r = parseFloat(arc.getAttribute('data-r'));
    // Skip arcs too small to measure — a sub-minimum radius (a rounded corner, say) is
    // too cramped to offer as a target (see MINIMUM_RADIUS_MM).
    if ([x, y, r].some(Number.isNaN) || r < MINIMUM_RADIUS_MM) { return; }
    // Aim the radius line at a point ON the arc (its midpoint along the path) so it
    // visibly touches the curve, not off into space. Circles need no edge — any
    // point at radius is on them (see radiusRulerFor's default). Sample the whole
    // curve too, so a click anywhere along the arc selects it.
    let edge = null;
    const curve = [];
    try {
      const total = arc.getTotalLength();
      for (let i = 0; i <= RADIUS_CURVE_SAMPLES; i += 1) {
        const point = arc.getPointAtLength((i / RADIUS_CURVE_SAMPLES) * total);
        curve.push({ x: point.x, y: point.y });
      }
      const midpoint = arc.getPointAtLength(total / 2);
      edge = { x: midpoint.x, y: midpoint.y };
    } catch (error) { /* no path geometry available — fall back to up-right, no curve */ }
    targets.push({ x, y, r, edge, curve });
  });
  return targets;
};

// Whether the drawing has any MEASURABLE circle/arc (at or above MINIMUM_RADIUS_MM) —
// the Radius type is offered only then. Uses the same threshold as gatherRadiusTargets
// but stops at the first qualifying shape (reading the DOM) rather than sampling every
// curve.
const hasRadiusTargets = () => {
  const measurable = (element, attribute) => parseFloat(element.getAttribute(attribute)) >= MINIMUM_RADIUS_MM;
  return Array.from(document.querySelectorAll(RADIUS_CIRCLE_SELECTOR)).some((circle) => measurable(circle, 'r'))
    || Array.from(document.querySelectorAll(RADIUS_ARC_SELECTOR)).some((arc) => measurable(arc, 'data-r'));
};

// The circle/arc centre nearest the cursor within a screen-pixel snap radius, or null.
const nearestCenter = (centers, clientX, clientY, matrix) => {
  let best = null;
  let bestDistance = CENTER_SNAP_PX;
  centers.forEach((center) => {
    const client = userToClient(center, matrix);
    if (!client) { return; }
    const distance = Math.hypot(client.x - clientX, client.y - clientY);
    if (distance <= bestDistance) {
      bestDistance = distance;
      best = center;
    }
  });
  return best;
};

// Whether a segment runs (nearly) along the horizontal ('h') or vertical ('v')
// axis, or is angled (null). A measurement may not run along the axis its
// dragged handle's edge follows, and the far handle never lands on an edge that
// runs along the measurement axis — that is what keeps the two ends off the
// same line.
const segmentAxisParallel = (segment) => {
  const dx = Math.abs(segment.bx - segment.ax);
  const dy = Math.abs(segment.by - segment.ay);
  if (dy <= dx * PARALLEL_RATIO) { return 'h'; }
  if (dx <= dy * PARALLEL_RATIO) { return 'v'; }
  return null;
};

const segmentLength = (segment) => Math.hypot(segment.bx - segment.ax, segment.by - segment.ay);
const segmentMidpoint = (segment) => ({ x: (segment.ax + segment.bx) / 2, y: (segment.ay + segment.by) / 2 });

// The closest point to (px,py) lying on the segment, clamped to its ends.
const closestPointOnSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;
  let t = lengthSquared === 0 ? 0 : ((px - ax) * dx + (py - ay) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return { x: ax + t * dx, y: ay + t * dy };
};

// The point on the dieline nearest the cursor (projection onto the closest
// edge), and which segment it lies on.
const nearestPointOnGeometry = (cursor, segments) => {
  let best = null;
  let bestIndex = -1;
  let bestDistance = Infinity;
  segments.forEach((segment, index) => {
    const point = closestPointOnSegment(cursor.x, cursor.y, segment.ax, segment.ay, segment.bx, segment.by);
    const distance = Math.hypot(point.x - cursor.x, point.y - cursor.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = point;
      bestIndex = index;
    }
  });
  return best ? { point: best, segmentIndex: bestIndex } : null;
};

// Where the horizontal/vertical line through `from` next meets an edge that
// does NOT run along that axis, nearest to `near`. Edges parallel to the
// measurement axis are skipped, so the far point is always on a different edge
// and the ruler crosses the interior rather than lying along a line. Returns
// null when no such crossing exists (e.g. at the extreme edge of the shape).
const alignedPointOnGeometry = (from, axis, segments, near) => {
  const candidates = [];
  const epsilon = 0.001;
  segments.forEach((segment) => {
    if (segmentAxisParallel(segment) === axis) { return; }
    if (axis === 'h') {
      const y = from.y;
      if (y < Math.min(segment.ay, segment.by) - epsilon || y > Math.max(segment.ay, segment.by) + epsilon) { return; }
      const t = (y - segment.ay) / (segment.by - segment.ay);
      candidates.push({ x: segment.ax + (segment.bx - segment.ax) * t, y });
    } else {
      const x = from.x;
      if (x < Math.min(segment.ax, segment.bx) - epsilon || x > Math.max(segment.ax, segment.bx) + epsilon) { return; }
      const t = (x - segment.ax) / (segment.bx - segment.ax);
      candidates.push({ x, y: segment.ay + (segment.by - segment.ay) * t });
    }
  });
  const distinct = candidates.filter((c) => Math.hypot(c.x - from.x, c.y - from.y) > COINCIDENT_MM);
  if (distinct.length === 0) { return null; }
  let best = distinct[0];
  let bestDistance = Infinity;
  distinct.forEach((candidate) => {
    const distance = Math.hypot(candidate.x - near.x, candidate.y - near.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  });
  return best;
};

// The axis a locked measurement may use from a handle on the given edge: the
// locked axis, unless the edge runs along it — a measurement can't lie on its own
// edge — in which case there is none (the last valid line is kept).
const allowedAxis = (draggedSegment, lockedAxis) =>
  (draggedSegment && segmentAxisParallel(draggedSegment) === lockedAxis) ? null : lockedAxis;

// Whether the ruler between a and b (running along `axis`) lies along an
// existing edge — i.e. it measures a section of a line, which is not a valid
// measurement. This catches the case where the two handles are on different
// edges but the straight line between them still coincides with an edge (e.g. a
// handle dragged to a corner). `a` and `b` share the off-axis coordinate.
const rulerOverlapsEdge = (a, b, axis, segments) => segments.some((segment) => {
  if (segmentAxisParallel(segment) !== axis) { return false; }
  if (axis === 'h') {
    if (Math.abs(segment.ay - a.y) > COINCIDENT_MM) { return false; }
    const overlap = Math.min(Math.max(a.x, b.x), Math.max(segment.ax, segment.bx))
      - Math.max(Math.min(a.x, b.x), Math.min(segment.ax, segment.bx));
    return overlap > COINCIDENT_MM;
  }
  if (Math.abs(segment.ax - a.x) > COINCIDENT_MM) { return false; }
  const overlap = Math.min(Math.max(a.y, b.y), Math.max(segment.ay, segment.by))
    - Math.max(Math.min(a.y, b.y), Math.min(segment.ay, segment.by));
  return overlap > COINCIDENT_MM;
});

// ---- Angled type: perpendicular distance between two crease lines ----
// Kept separate from the h/v axis code above (which stays untouched). The
// measurement direction is derived from a crease's own angle, so the ruler
// crosses it at 90° by construction.

const segmentUnit = (segment) => {
  const dx = segment.bx - segment.ax;
  const dy = segment.by - segment.ay;
  const length = Math.hypot(dx, dy);
  return length === 0 ? { x: 1, y: 0 } : { x: dx / length, y: dy / length };
};

// Where the infinite line through `from` along unit `dir` crosses `segment`
// within its extent, or null when the segment is parallel to `dir` or the
// crossing falls outside it.
const rayCrossSegment = (from, dir, segment) => {
  const perpX = -dir.y;
  const perpY = dir.x;
  const edgeX = segment.bx - segment.ax;
  const edgeY = segment.by - segment.ay;
  const denominator = (edgeX * perpX) + (edgeY * perpY);
  if (Math.abs(denominator) < 1e-9) { return null; }
  const u = -(((segment.ax - from.x) * perpX) + ((segment.ay - from.y) * perpY)) / denominator;
  if (u < -1e-6 || u > 1 + 1e-6) { return null; }
  return { x: segment.ax + (u * edgeX), y: segment.ay + (u * edgeY) };
};

// From `anchor` on `anchorCrease`, the point where the perpendicular to that
// crease meets the NEAREST other crease (searched both ways) — the far end of a
// 90°-to-the-crease measurement. Returns { a: anchor, b } or null.
const perpendicularAcross = (anchor, anchorCrease, creases) => {
  const unit = segmentUnit(anchorCrease);
  const dir = { x: -unit.y, y: unit.x };
  let best = null;
  let bestDistance = Infinity;
  creases.forEach((crease) => {
    if (crease === anchorCrease) { return; }
    const hit = rayCrossSegment(anchor, dir, crease);
    if (!hit) { return; }
    const distance = Math.hypot(hit.x - anchor.x, hit.y - anchor.y);
    if (distance > COINCIDENT_MM && distance < bestDistance) { bestDistance = distance; best = hit; }
  });
  return best ? { a: anchor, b: best } : null;
};

// A default angled measurement: the crease nearest the viewport centre, anchored
// at its midpoint, measured perpendicular to its nearest neighbouring crease.
// Falls back to other creases until one has a neighbour across. Exported for tests.
export const findAngledRuler = (creases, viewportCenter) => {
  if (creases.length < 2) { return null; }
  const ordered = viewportCenter
    ? [...creases].sort((first, second) => {
      const firstMid = segmentMidpoint(first);
      const secondMid = segmentMidpoint(second);
      return Math.hypot(firstMid.x - viewportCenter.x, firstMid.y - viewportCenter.y)
        - Math.hypot(secondMid.x - viewportCenter.x, secondMid.y - viewportCenter.y);
    })
    : creases;
  let ruler = null;
  ordered.some((crease) => {
    const found = perpendicularAcross(segmentMidpoint(crease), crease, creases);
    if (found) { ruler = { ...found, crease }; return true; }
    return false;
  });
  return ruler;
};

// ---- Radius type: a circle/arc's radius, centre to edge ----

// The distance (per unit radius) the edge sits along each axis when the radius
// line is drawn up-right (a 45° line: cos 45° = sin 45° = √0.5).
const RADIUS_EDGE_UNIT = Math.sqrt(0.5);

// The centre/edge for a radius target. Arcs carry an `edge` on their own curve (so
// the line touches the arc); circles have none, so the edge sits one radius up-right
// of the centre (SVG y is down, so "up" is -y), matching the server's up-right render.
const radiusRulerFor = (target) => ({
  center: { x: target.x, y: target.y },
  radius: target.r,
  edge: target.edge || { x: target.x + (target.r * RADIUS_EDGE_UNIT), y: target.y - (target.r * RADIUS_EDGE_UNIT) },
});

// The default Radius measurement: the circle/arc nearest the viewport centre.
// Returns { center, radius, edge } or null when there are no targets. Exported
// for tests.
export const findRadiusTarget = (targets, viewportCenter) => {
  if (!targets || targets.length === 0) { return null; }
  const ordered = viewportCenter
    ? [...targets].sort((first, second) =>
      Math.hypot(first.x - viewportCenter.x, first.y - viewportCenter.y)
        - Math.hypot(second.x - viewportCenter.x, second.y - viewportCenter.y))
    : targets;
  return radiusRulerFor(ordered[0]);
};

// Perpendicular distance from a point to a segment (screen px), for grabbing the
// dimension line and for hit-testing a click against a curve.
const distanceToSegment = (px, py, ax, ay, bx, by) => {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = (dx * dx) + (dy * dy);
  let t = lengthSquared === 0 ? 0 : (((px - ax) * dx) + ((py - ay) * dy)) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + (t * dx)), py - (ay + (t * dy)));
};

// The circle/arc whose CURVE is nearest the click (in screen space) within
// `maxDistancePx` — pass Infinity to always take the nearest — as a
// { center, radius, edge } ruler. So a shape is selected by clicking ON its
// arc/circle, and a drag snaps to the nearest one. Distance is measured to the curve's
// polyline SEGMENTS (not just the sampled points), so there are no dead spots between
// samples on a large or zoomed shape. `project` maps a user-space point to client
// { x, y } (null when it can't be mapped). Exported for testing.
export const radiusTargetNearestCurve = (targets, clientX, clientY, project, maxDistancePx) => {
  let best = null;
  let bestDistance = maxDistancePx;
  targets.forEach((target) => {
    const samples = target.curve || [];
    for (let i = 0; i < samples.length - 1; i += 1) {
      const from = project(samples[i]);
      const to = project(samples[i + 1]);
      if (!from || !to) { continue; }
      const distance = distanceToSegment(clientX, clientY, from.x, from.y, to.x, to.y);
      if (distance <= bestDistance) { bestDistance = distance; best = target; }
    }
  });
  return best ? radiusRulerFor(best) : null;
};

// The default ruler for a set of segments: a start point and the point straight
// across the shape from it ({ from, across }), or null when no pair of edges
// faces across. Prefers a start near `viewportCenter` so the ruler lands on
// screen; a mostly-angled or curved dieline can have no edge straight across
// from that first point, so it falls back to every edge's midpoint — axis-
// parallel edges first, then longest — until one measures cleanly across.
// `viewportCenter` may be null when the viewport can't be read, in which case
// each start measures its widest span. When `lockedAxis` ('h' or 'v') is given
// the ruler is forced to that axis; without it either axis is allowed (the start
// edge's perpendicular preferred). Exported for testing.
export const findDefaultRuler = (segments, viewportCenter, lockedAxis) => {
  if (segments.length < 1) { return null; }

  // The across point for a start: the first allowed axis whose straight line
  // through `from` meets a real, non-overlapping edge, or null when none works
  // from that start. Locked to `lockedAxis` when given, else the start edge's
  // perpendicular is tried first.
  const acrossFrom = (from, startSegment) => {
    const preferred = segmentAxisParallel(startSegment) === 'h' ? 'v' : 'h';
    const axes = lockedAxis ? [lockedAxis] : [preferred, preferred === 'h' ? 'v' : 'h'];
    let across = null;
    axes.some((candidateAxis) => {
      const target = viewportCenter || (candidateAxis === 'h'
        ? { x: Number.MAX_SAFE_INTEGER, y: from.y }
        : { x: from.x, y: -Number.MAX_SAFE_INTEGER });
      const point = alignedPointOnGeometry(from, candidateAxis, segments, target);
      if (point && !rulerOverlapsEdge(from, point, candidateAxis, segments)) { across = point; return true; }
      return false;
    });
    return across;
  };

  const starts = [];
  const hit = viewportCenter && nearestPointOnGeometry(viewportCenter, segments);
  if (hit) { starts.push({ from: hit.point, segment: segments[hit.segmentIndex] }); }
  [...segments]
    .sort((a, b) => {
      const straight = (segmentAxisParallel(a) ? 0 : 1) - (segmentAxisParallel(b) ? 0 : 1);
      return straight !== 0 ? straight : segmentLength(b) - segmentLength(a);
    })
    .forEach((segment) => starts.push({ from: segmentMidpoint(segment), segment }));

  let ruler = null;
  starts.some(({ from, segment }) => {
    const across = acrossFrom(from, segment);
    if (across) { ruler = { from, across }; return true; }
    return false;
  });
  return ruler;
};

const greatestCommonDivisor = (a, b) => (b === 0 ? a : greatestCommonDivisor(b, a % b));

const formatFractionalInch = (inches) => {
  const denominator = 16;
  const totalSixteenths = Math.round(inches * denominator);
  const whole = Math.floor(totalSixteenths / denominator);
  let numerator = totalSixteenths - whole * denominator;
  if (numerator === 0) { return `${whole}"`; }
  const divisor = greatestCommonDivisor(numerator, denominator);
  numerator /= divisor;
  const reducedDenominator = denominator / divisor;
  if (whole === 0) { return `${numerator}/${reducedDenominator}"`; }
  return `${whole} ${numerator}/${reducedDenominator}"`;
};

// Where a witness line stops: WITNESS_GAP_MM short of the measured point `to`, back
// toward the dimension-line end `from`, so the extension line doesn't touch the
// geometry. null when the point is within the gap of the line (nothing to draw).
const witnessEnd = (from, to) => {
  const dx = from.x - to.x;
  const dy = from.y - to.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= WITNESS_GAP_MM) { return null; }
  const fraction = WITNESS_GAP_MM / distance;
  return { x: to.x + (dx * fraction), y: to.y + (dy * fraction) };
};

// A floppy-disk "save" icon (Feather "save"), `size` px, centred at (centerX, 0)
// and drawn in the brand colour to sit on the white Save button.
const saveIcon = (centerX, size) => {
  const scale = size / 24;
  return (
    <g key="save-icon" className={styles.addCtaIcon}
      transform={`translate(${centerX - (size / 2)}, ${-(size / 2)}) scale(${scale})`}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </g>
  );
};

// The axis icon for a measurement type: a double-headed arrow along the axis the
// ruler runs (↔ horizontal, ↕ vertical), shown on that type's toolbar button.
const AXIS_ICON_PATHS = {
  horizontal: (
    <>
      <line x1="3" y1="8" x2="13" y2="8" />
      <polyline points="5.5,5.5 3,8 5.5,10.5" />
      <polyline points="10.5,5.5 13,8 10.5,10.5" />
    </>
  ),
  vertical: (
    <>
      <line x1="8" y1="3" x2="8" y2="13" />
      <polyline points="5.5,5.5 8,3 10.5,5.5" />
      <polyline points="5.5,10.5 8,13 10.5,10.5" />
    </>
  ),
  angled: (
    <>
      <line x1="4" y1="12" x2="12" y2="4" />
      <polyline points="4,7.5 4,12 8.5,12" />
      <polyline points="7.5,4 12,4 12,8.5" />
    </>
  ),
  radius: (
    <>
      <circle cx="7" cy="8" r="5" />
      <line x1="7" y1="8" x2="12" y2="8" />
      <circle cx="7" cy="8" r="1" fill="currentColor" stroke="none" />
    </>
  ),
};

const axisIcon = (type) => (
  <svg className={styles.typeIcon} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
      {AXIS_ICON_PATHS[type] || AXIS_ICON_PATHS.horizontal}
    </g>
  </svg>
);

// A live readout only — the saved annotation (later phase) is formatted
// server-side by DimensionText, which stays the authoritative value.
const formatMeasurement = (millimeters, unit) => {
  if (unit === 'mm') { return `${millimeters.toFixed(2)} mm`; }
  const inches = millimeters / MM_PER_INCH;
  if (unit === 'infr') { return formatFractionalInch(inches); }
  return `${inches.toFixed(3)}"`;
};

const MeasureTool = ({ svg, unit, theme, drawingId, loggedIn }) => {
  const [active, setActive] = useState(false);
  // The locked measurement type ('horizontal' | 'vertical'), remembered across
  // sessions. Mirrored into a ref so the pointer handlers (which must not go
  // stale) read the current value; `setMeasurementType` keeps both in step.
  const [measurementType, setMeasurementTypeState] = useState(readStoredMeasurementType);
  const measurementTypeRef = useRef(measurementType);
  // `persist` remembers the type as the last-used one; a type forced by loading a
  // saved annotation for editing passes false, so it doesn't overwrite the type
  // the user last deliberately picked.
  const setMeasurementType = (type, { persist = true } = {}) => {
    measurementTypeRef.current = type;
    setMeasurementTypeState(type);
    if (persist) { storeMeasurementType(type); }
  };
  const [menuOpen, setMenuOpen] = useState(false);
  // Mirrors menuOpen for the active-tool Escape handler (a stale-closure effect)
  // so it can skip resetting the ruler while the dropdown is open.
  const menuOpenRef = useRef(menuOpen);
  menuOpenRef.current = menuOpen;
  // The user's saved annotations on this drawing at the current dimensions, each
  // { id, mid_x, mid_y } — where its remove control is placed.
  const [annotations, setAnnotations] = useState([]);
  // Whether the drawing has a measurable circle/arc (at least MINIMUM_RADIUS_MM) — the
  // Radius type is offered only then. Tracked in state so the dropdown updates without
  // opening the tool.
  const [radiusAvailable, setRadiusAvailable] = useState(false);
  // Shown when Done is pressed with an unsaved, positioned measurement — a confirm
  // popup so the in-progress measurement isn't silently discarded.
  const [confirmClose, setConfirmClose] = useState(false);
  // Interaction lives in refs (it must not go stale inside the pointer
  // handlers); `tick` is bumped to re-render the overlay after a ref changes or
  // the view pans/zooms.
  const [, setTick] = useState(0);
  const rerender = () => setTick((value) => value + 1);

  const overlayRef = useRef(null);
  const dropdownRef = useRef(null);
  const segmentsRef = useRef([]);
  const snapPointsRef = useRef([]);
  const radiusTargetsRef = useRef([]);
  const handleARef = useRef(null);
  const handleBRef = useRef(null);
  const dragRef = useRef(null); // { name, axis, startX, startY } | null
  // Whether the cursor is over a selectable arc/circle in Radius mode (no drag in
  // progress), so the overlay shows a pointer cursor — signalling the curves are
  // clickable.
  const radiusHoverRef = useRef(false);
  // How far the dimension line is moved off the measured points, perpendicular to
  // the axis (SVG space); 0 = through the points, dragging the line changes it.
  const offsetRef = useRef(0);
  // The id of the saved annotation being edited, or null when measuring a fresh
  // one — decides whether saving updates in place or creates.
  const editingIdRef = useRef(null);
  // Whether the tool was already active on the previous effect run, so an SVG
  // swap (a redraw) can be told apart from the tool being switched on.
  const wasActiveRef = useRef(false);
  // Manage mode: the overlay is open only to edit/remove existing measurements —
  // no new ruler is placed. Entered from the "Edit measurements" toolbar button;
  // left by picking a type (start a new one), editing one, or Done/Escape. A ref
  // (not state) so the activation effect and pointer/render paths read it without
  // going stale; every change to it is paired with a re-render trigger.
  const manageRef = useRef(false);
  // Whether the live ruler holds an unsaved measurement the user has positioned
  // (dragged a handle, slid it, or picked a shape) — as opposed to an untouched
  // default. Done confirms before discarding one; a fresh default or a save clears it.
  const dirtyRef = useRef(false);
  // Whether the visitor is signed in and so can save. Seeded from the loggedIn prop
  // (correct at mount, unlike window.loggedIn which ShowDrawing fills in asynchronously),
  // but a signed-out visitor on the demo signs in through the shared popup WITHOUT a
  // page reload (the AJAX path), which leaves the prop stale — so flip this ONLY on the
  // user.session_started event a real sign-in emits, and read it (not the prop) at save
  // time so a later save doesn't re-open the popup for an already signed-in visitor.
  const signedInRef = useRef(loggedIn);
  // Set when a signed-out visitor asks to save: the save is finished once they actually
  // sign in (the user.session_started event), never on the popup merely closing.
  const pendingSaveRef = useRef(false);
  // The latest addToDrawing, so the sign-in retry runs the current closure (current
  // geometry/drawing) rather than a stale one captured when the listener was attached.
  const addToDrawingRef = useRef(null);

  // Seat a valid measurement across the drawing so the ruler is visible
  // immediately: a point on the longest vertical edge measured across to the
  // opposite side (or the longest horizontal edge measured vertically).
  // The centre of what's currently on screen, in the geometry's own space, or
  // null when the overlay/geometry matrix isn't ready yet.
  const viewportCenterPoint = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix) { return null; }
    const rect = overlay.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) { return null; }
    return clientToUser(rect.left + (rect.width / 2), rect.top + (rect.height / 2), matrix);
  };

  const isAngled = () => measurementTypeRef.current === 'angled';
  const isRadius = () => measurementTypeRef.current === 'radius';
  const creaseSegments = () => segmentsRef.current.filter((segment) => segment.crease);

  const placeDefaultRuler = () => {
    // A fresh default is not a measurement the user has made, so it needs no confirm.
    dirtyRef.current = false;
    if (isRadius()) {
      seatRadius(findRadiusTarget(radiusTargetsRef.current, viewportCenterPoint()));
      return;
    }
    if (isAngled()) {
      const ruler = findAngledRuler(creaseSegments(), viewportCenterPoint());
      handleARef.current = ruler ? { x: ruler.a.x, y: ruler.a.y, crease: ruler.crease } : null;
      handleBRef.current = ruler ? { x: ruler.b.x, y: ruler.b.y } : null;
      return;
    }
    const ruler = findDefaultRuler(segmentsRef.current, viewportCenterPoint(), axisForType(measurementTypeRef.current));
    handleARef.current = ruler ? ruler.from : null;
    handleBRef.current = ruler ? ruler.across : null;
  };

  // Seat a radius ruler on the handles: A holds the centre (and the shape's radius,
  // sent on save so the server picks the right shape when several are concentric),
  // B holds the edge. null clears both (no circle/arc to measure).
  const seatRadius = (ruler) => {
    handleARef.current = ruler ? { x: ruler.center.x, y: ruler.center.y, radius: ruler.radius } : null;
    handleBRef.current = ruler ? { x: ruler.edge.x, y: ruler.edge.y } : null;
  };

  // Switch the locked orientation and re-seat a fresh ruler in it. Switching is a
  // deliberate toolbar action, never a side effect of dragging — so a measurement
  // can't flip axis by accident, which is the whole point of the locked types.
  const chooseType = (type) => {
    if (type === measurementTypeRef.current) { return; }
    const wasEditing = editingIdRef.current;
    setMeasurementType(type);
    placeDefaultRuler();
    offsetRef.current = 0;
    editingIdRef.current = null;
    dragRef.current = null;
    // Switching type abandons an in-progress edit; the annotation was hidden
    // while it was the live ruler, so re-fetch to bring it back with its controls.
    if (wasEditing) { fetchAnnotations(); }
    rerender();
  };

  // Pick a type from the dropdown: start the tool in it when the tool is off,
  // otherwise switch the live ruler to it. Either way the dropdown closes.
  const selectType = (type) => {
    setMenuOpen(false);
    if (active && !manageRef.current) {
      chooseType(type);
    } else if (active && manageRef.current) {
      // Leaving manage mode to CREATE a new measurement: seat a fresh ruler in the
      // picked type now (the activation effect won't re-run — the tool is already on).
      manageRef.current = false;
      setMeasurementType(type);
      placeDefaultRuler();
      offsetRef.current = 0;
      editingIdRef.current = null;
      dragRef.current = null;
      rerender();
    } else {
      trackEvent('measure_tool_opened', {});
      setMeasurementType(type);
      setActive(true);
    }
  };

  // Open the overlay to manage existing measurements only — no new ruler is placed.
  // Each saved measurement shows its edit/remove controls; picking a type (start a
  // new one), editing one, or Done/Escape leaves manage mode.
  const enterManageMode = () => {
    setMenuOpen(false);
    trackEvent('measure_tool_opened', {});
    manageRef.current = true;
    handleARef.current = null;
    handleBRef.current = null;
    editingIdRef.current = null;
    dragRef.current = null;
    offsetRef.current = 0;
    setActive(true);
  };

  // Arrow-key navigation between the open dropdown's items (the ARIA menu pattern).
  const onMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') { return; }
    event.preventDefault();
    const items = Array.from(dropdownRef.current?.querySelectorAll('[role="menuitem"]') || []);
    if (items.length === 0) { return; }
    const current = items.indexOf(document.activeElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(current + step + items.length) % items.length].focus();
  };

  // The user's saved annotations, positioned at the current dimensions so each
  // remove control lands on its annotation. Options must match the render the
  // annotations were injected into, so pass the current drawing options.
  const fetchAnnotations = () => {
    if (!drawingId) { return; }
    // A signed-out visitor (the demo shows them the tool) has no saved annotations,
    // and the endpoint is signed-in-only, so skip the fetch — it would just 403.
    if (!signedInRef.current) { return; }
    // Signed in, but the render reported no saved measurements here (the common
    // case) — nothing to place. Only an explicit false skips: when the flag is
    // unknown (before the first redraw sets it) we still fetch, so annotations are
    // never silently hidden. A save flips it true; a redraw refreshes it.
    if (window.currentDrawingHasMeasureAnnotations === false) { setAnnotations([]); return; }
    const query = new URLSearchParams();
    // Serialise the FULL options — arrays and nested objects included, in Rails
    // bracket notation — so the list redraws the SAME geometry the SVG/PDF render
    // injects into. Dropping non-scalar options (e.g. the `add_ons` array) made
    // the list redraw a different drawing, so annotations on that geometry got no
    // control and every control's position was computed from the wrong geometry.
    const appendOption = (name, value) => {
      if (value === null || value === undefined) { return; }
      if (Array.isArray(value)) {
        value.forEach((item) => appendOption(`${name}[]`, item));
      } else if (typeof value === 'object') {
        Object.entries(value).forEach(([key, nested]) => appendOption(`${name}[${key}]`, nested));
      } else {
        query.append(name, value);
      }
    };
    Object.entries(window.currentDrawingOptions || {}).forEach(([key, value]) => appendOption(`options[${key}]`, value));
    // The drawing's span from the render that drew the current SVG (set by
    // DrawingSvg.onRedrawSuccess). The saved offset is a fraction of it, so passing
    // the render's own value lets the server place each control on its drawn
    // annotation without redrawing the whole drawing again to recompute it.
    const span = window.currentDrawingSpan;
    if (span) {
      if (span.width != null) { query.append('span_width', span.width); }
      if (span.height != null) { query.append('span_height', span.height); }
    }
    fetch(`/measure-tool/${drawingId}/annotations?${query.toString()}`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : { annotations: [] }))
      // Exclude the annotation currently being edited: it IS the live ruler, so its
      // own edit/remove controls stay hidden (loadAnnotation removed it) until
      // editing ends — otherwise a redraw's re-fetch would re-add them on top of it.
      .then((data) => setAnnotations((data.annotations || []).filter((annotation) => annotation.id !== editingIdRef.current)))
      .catch(() => { /* keep whatever list we have */ });
  };

  // Remove one of the user's own annotations, then redraw so the server drops it
  // from the render. Clear it optimistically so its control disappears at once.
  const removeAnnotation = (annotationId) => {
    if (!drawingId) { return; }
    const token = document.querySelector('meta[name=csrf-token]')?.getAttribute('content');
    fetch(`/measure-tool/${drawingId}/annotations/${annotationId}`, {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': token },
    }).then((response) => {
      if (response.ok) {
        trackEvent('measure_annotation_removed', {});
        const remaining = annotations.filter((annotation) => annotation.id !== annotationId);
        setAnnotations(remaining);
        document.dispatchEvent(new CustomEvent('measure_tool.annotation_changed'));
        // In manage mode, close the tool once the last measurement is gone — there
        // is nothing left to manage.
        if (manageRef.current && remaining.length === 0) {
          manageRef.current = false;
          setActive(false);
        }
      }
    });
  };

  // Re-open a saved annotation as the live ruler so it can be repositioned or
  // re-measured; its id is remembered so saving updates it in place. The server
  // sends drawing-space coordinates, so the SVG's flipped y is restored here.
  const loadAnnotation = (annotation) => {
    // Both ends are deliberate (they were saved), so keep them pinned on edit.
    const a = { x: annotation.ax, y: -annotation.ay, placed: true };
    const b = { x: annotation.bx, y: -annotation.by, placed: true };
    handleARef.current = a;
    handleBRef.current = b;
    // Lock the tool to the saved annotation's own type while editing it, and restore
    // the offset in the client's convention (y-sign flipped for horizontal/angled;
    // the server already resolved the angled offset into that convention).
    const stored = annotation.offset || 0;
    if (annotation.measurement_type === 'radius') {
      // a is the shape's centre and b a point on its edge — both resolved server-side
      // for THIS annotation's own shape at the current dimensions. Seat the ruler
      // straight from them; re-matching by nearest centre would bind a concentric
      // shape (same centre, different radius) to the wrong one. A radial dimension has
      // no perpendicular offset, so it always sits on its radius.
      setMeasurementType('radius', { persist: false });
      seatRadius({ center: { x: a.x, y: a.y }, radius: Math.hypot(b.x - a.x, b.y - a.y), edge: { x: b.x, y: b.y } });
      offsetRef.current = 0;
    } else if (annotation.measurement_type === 'angled') {
      setMeasurementType('angled', { persist: false });
      offsetRef.current = stored;
    } else {
      const horizontal = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y);
      setMeasurementType(horizontal ? 'horizontal' : 'vertical', { persist: false });
      offsetRef.current = horizontal ? -stored : stored;
    }
    editingIdRef.current = annotation.id;
    dragRef.current = null;
    manageRef.current = false;
    // The loaded ruler starts as the saved state; only a change makes it dirty (and
    // the saved annotation survives a Done anyway, since it's only re-fetched).
    dirtyRef.current = false;
    // Hide its controls while it is the live ruler.
    setAnnotations((current) => current.filter((item) => item.id !== annotation.id));
    rerender();
  };

  // A signed-out visitor who signs in from the Save popup does so without reloading, so
  // learn about it here, and finish the save they asked for. This fires only on a real
  // sign-in (SignIn/SignUp emit it) — NOT when the popup is merely dismissed — so closing
  // the popup without signing in leaves the visitor signed-out and saves nothing.
  useEffect(() => {
    const onSessionStarted = () => {
      signedInRef.current = true;
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        addToDrawingRef.current?.();
      }
    };
    document.addEventListener('user.session_started', onSessionStarted);
    return () => { document.removeEventListener('user.session_started', onSessionStarted); };
  }, []);

  // Re-gather the geometry whenever it changes (a redraw replaces the SVG) or
  // the tool is switched on. Re-seat the default ruler when the tool is switched
  // ON, or when the DIELINE GEOMETRY actually changed (a dimensions redraw). An
  // annotation add/remove redraws the SAME geometry, so the in-progress ruler is
  // kept — removing one annotation must not discard the measurement in progress.
  useEffect(() => {
    if (!active) {
      wasActiveRef.current = false;
      return;
    }
    const justActivated = !wasActiveRef.current;
    wasActiveRef.current = true;
    const previousSegments = segmentsRef.current;
    segmentsRef.current = gatherSegments();
    snapPointsRef.current = gatherSnapPoints();
    radiusTargetsRef.current = gatherRadiusTargets();
    fetchAnnotations();
    // In manage mode the overlay opens with no ruler — only the saved measurements'
    // edit/remove controls — so don't seat a default ruler.
    if ((justActivated || !sameSegments(previousSegments, segmentsRef.current)) && !manageRef.current) {
      placeDefaultRuler();
      dragRef.current = null;
      offsetRef.current = 0;
      editingIdRef.current = null;
    }
    rerender();
  }, [active, svg]);

  // Keep the saved-measurement count known while the tool is CLOSED, so the
  // "Edit measurements" button can appear only when there is something to manage
  // (when the tool is open, the activation effect above keeps them fresh).
  useEffect(() => {
    if (!active && drawingId) { fetchAnnotations(); }
  }, [active, svg]);

  // The Radius type is offered only when the drawing has a MEASURABLE circle or arc
  // (at least MINIMUM_RADIUS_MM); re-check on each redraw (a dimensions change can add
  // or remove them, or move one across the minimum).
  useEffect(() => {
    setRadiusAvailable(hasRadiusTargets());
  }, [svg]);

  // While the tool is active, dim the rest of the dieline so the ruler stands
  // out. The class is re-applied on `svg` change (a redraw injects a new #svg).
  useEffect(() => {
    const target = svgRoot();
    if (!active || !target) { return undefined; }
    target.classList.add(styles.dimmed);
    return () => { target.classList.remove(styles.dimmed); };
  }, [active, svg]);

  // While the type dropdown is open: focus the first item (keyboard entry), close
  // on an outside click, and close on Escape (returning focus to the toggle). The
  // active-tool Escape handler skips its ruler reset while the menu is open
  // (menuOpenRef), so Escape closes only the menu without a global stopPropagation.
  useEffect(() => {
    if (!menuOpen) { return undefined; }
    dropdownRef.current?.querySelector('[role="menuitem"]')?.focus();
    const onPointerDownOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target)) { setMenuOpen(false); }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        dropdownRef.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDownOutside, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDownOutside, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  // Keep the overlay aligned with the geometry as the user pans/zooms/scrolls.
  useEffect(() => {
    if (!active) { return undefined; }

    const root = svgRoot();
    let observer = null;
    if (root && typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(rerender);
      observer.observe(root, { attributes: true, attributeFilter: ['transform'], subtree: true });
    }
    window.addEventListener('resize', rerender);
    window.addEventListener('scroll', rerender, true);

    const onKeyDown = (event) => {
      // While the type dropdown is open, Escape closes it (handled elsewhere), not
      // the ruler.
      if (event.key === 'Escape' && !menuOpenRef.current) {
        // In manage mode Escape closes the tool (there is no ruler to reset);
        // otherwise it re-seats the default ruler.
        if (manageRef.current) {
          manageRef.current = false;
          setActive(false);
        } else {
          placeDefaultRuler();
          offsetRef.current = 0;
        }
        rerender();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    // React's onWheel is passive, so preventDefault there is ignored. Attach a
    // native non-passive listener so mouse-wheel zoom can be forwarded to
    // pan-zoom (and the page kept from scrolling) while the overlay is active.
    const overlay = overlayRef.current;
    const onWheel = (event) => {
      const instance = window.svgPanZoomInstance;
      const target = svgRoot();
      if (!instance || !target) { return; }
      event.preventDefault();
      const rect = target.getBoundingClientRect();
      // Zoom proportional to the scroll amount, normalised across wheel/trackpad
      // and clamped. A fixed step per event compounded wildly on a trackpad, which
      // fires many events per gesture — the cause of the runaway zoom.
      let delta = event.deltaY;
      if (event.deltaMode === 1) { delta *= 16; }
      else if (event.deltaMode === 2) { delta *= rect.height; }
      delta = Math.max(-100, Math.min(100, delta));
      const factor = Math.exp(-delta * 0.0015);
      instance.zoomAtPoint(instance.getZoom() * factor, {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      rerender();
    };
    if (overlay) { overlay.addEventListener('wheel', onWheel, { passive: false }); }

    return () => {
      if (observer) { observer.disconnect(); }
      window.removeEventListener('resize', rerender);
      window.removeEventListener('scroll', rerender, true);
      window.removeEventListener('keydown', onKeyDown);
      if (overlay) { overlay.removeEventListener('wheel', onWheel); }
    };
  }, [active, svg]);

  const handleUnderCursor = (clientX, clientY, matrix) => {
    const candidates = [['A', handleARef.current], ['B', handleBRef.current]];
    let grabbed = null;
    let bestDistance = GRAB_RADIUS_PX;
    candidates.forEach(([name, point]) => {
      const client = userToClient(point, matrix);
      if (!client) { return; }
      const distance = Math.hypot(client.x - clientX, client.y - clientY);
      if (distance <= bestDistance) {
        bestDistance = distance;
        grabbed = name;
      }
    });
    return grabbed;
  };

  // The circle/arc whose CURVE is under the click, as a { center, radius, edge }
  // ruler — so clicking ON an arc/circle selects that shape. Delegates to the
  // exported curve hit-test, projecting each curve sample through the geometry CTM.
  const radiusTargetUnderCursor = (clientX, clientY, matrix) =>
    radiusTargetNearestCurve(radiusTargetsRef.current, clientX, clientY, (point) => userToClient(point, matrix), GRAB_RADIUS_PX);

  // The ruler's orientation is the locked measurement type, not something derived
  // from the handle positions — so it never flips while dragging.
  const isHorizontalRuler = () => measurementTypeRef.current === 'horizontal';

  // Endpoints of the dimension line in SVG user space, or null. The line is ALWAYS
  // perfectly horizontal or vertical. It sits at the FIRST handle's level (plus the
  // drag offset), so when the two points are not aligned the second projects to the
  // intersection corner — an L with a witness leg to that point — rather than
  // floating at their midpoint. For aligned points both share the level, so it is
  // unchanged. Shared by the render and the grab-detection so they always agree.
  const dimensionLineUserEnds = () => {
    const a = handleARef.current;
    const b = handleBRef.current;
    if (!a || !b) { return null; }
    // Radius: the radial line lies ON the radius (centre → a point on the arc/circle
    // edge). It has no perpendicular offset — a radial dimension stays on its radius.
    if (isRadius()) {
      return { start: { x: a.x, y: a.y }, end: { x: b.x, y: b.y } };
    }
    // Angled: the ruler runs from crease to crease. The offset slides it PERPENDICULAR
    // to that line — keeping its angle and length — off the geometry, with witness
    // lines connecting it back.
    if (isAngled()) {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy) || 1;
      const perpX = -dy / length;
      const perpY = dx / length;
      const shift = offsetRef.current;
      return {
        start: { x: a.x + (perpX * shift), y: a.y + (perpY * shift) },
        end: { x: b.x + (perpX * shift), y: b.y + (perpY * shift) },
      };
    }
    const horizontal = isHorizontalRuler();
    const lineCoord = (horizontal ? a.y : a.x) + offsetRef.current;
    return {
      start: horizontal ? { x: a.x, y: lineCoord } : { x: lineCoord, y: a.y },
      end: horizontal ? { x: b.x, y: lineCoord } : { x: lineCoord, y: b.y },
    };
  };

  // The same endpoints in client space, for grabbing the dimension line.
  const dimensionLineEnds = (matrix) => {
    const ends = dimensionLineUserEnds();
    if (!ends) { return null; }
    const start = userToClient(ends.start, matrix);
    const end = userToClient(ends.end, matrix);
    return (start && end) ? { start, end } : null;
  };

  // Start dragging the dimension line perpendicular (out of the drawing). Shared
  // by the ruler line's grab zone and the value label, so the whole label is a
  // move target — not only the sliver that happens to sit within the thin line's
  // grab radius (a vertical measurement's label sits off to the side).
  const beginOffsetDrag = (event) => {
    try { overlayRef.current?.setPointerCapture(event.pointerId); } catch (error) { /* no-op */ }
    const cursor = clientToUser(event.clientX, event.clientY, geometryMatrix());
    if (!cursor) { return; }
    const drag = {
      name: 'offset', horizontal: isHorizontalRuler(),
      startOffset: offsetRef.current, startUserX: cursor.x, startUserY: cursor.y,
    };
    // Angled offsets slide perpendicular to the A→B line (keeping its angle), so the
    // drag is measured along that perpendicular rather than a fixed screen axis.
    if (isAngled()) {
      const a = handleARef.current;
      const b = handleBRef.current;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const length = Math.hypot(dx, dy) || 1;
      drag.perpendicular = { x: -dy / length, y: dx / length };
    }
    dragRef.current = drag;
    rerender();
  };

  // Start dragging the whole radius measurement. As the cursor moves it snaps to the
  // arc/circle nearest the cursor (see onPointerMove), so a measurement can be dragged
  // straight onto another arc. Grabbed from the radial line, the value pill, or an arc.
  const beginRadiusMove = (event) => {
    try { overlayRef.current?.setPointerCapture(event.pointerId); } catch (error) { /* no-op */ }
    dragRef.current = { name: 'radius-move' };
    rerender();
  };

  const onPointerDown = (event) => {
    const matrix = geometryMatrix();
    // Pointer capture keeps the drag alive if the cursor leaves the overlay; it
    // is only a nicety, so ignore environments where it is unavailable.
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch (error) { /* no-op */ }

    // Radius: clicking ON an arc/circle curve selects it; grabbing the arc, the radial
    // line, or the value pill (its own handler) starts a move-drag that snaps the
    // measurement to the arc nearest the cursor — so you can drag a measurement onto
    // another arc. Empty space pans. The line stays on its radius (no perpendicular
    // slide) and there are no draggable handles.
    if (isRadius()) {
      const ruler = radiusTargetUnderCursor(event.clientX, event.clientY, matrix);
      if (ruler) {
        seatRadius(ruler);
        beginRadiusMove(event);
        return;
      }
      const radiusEnds = dimensionLineEnds(matrix);
      const onRadiusLine = radiusEnds && distanceToSegment(event.clientX, event.clientY, radiusEnds.start.x, radiusEnds.start.y, radiusEnds.end.x, radiusEnds.end.y) <= GRAB_RADIUS_PX;
      if (onRadiusLine) { beginRadiusMove(event); return; }
      dragRef.current = { name: 'pan', lastX: event.clientX, lastY: event.clientY };
      rerender();
      return;
    }

    // Grabbing a handle changes the measurement.
    const grabbed = handleUnderCursor(event.clientX, event.clientY, matrix);
    if (grabbed) {
      // Snapshot both ends so an invalid drop can restore the last valid line.
      dragRef.current = {
        name: grabbed, axis: null, startX: event.clientX, startY: event.clientY,
        prevA: handleARef.current, prevB: handleBRef.current,
      };
      rerender();
      return;
    }

    // Grabbing the dimension line moves it perpendicular (out of the drawing). For
    // angled the displacement is perpendicular to the A→B line (keeping its angle);
    // for horizontal/vertical it is along the locked axis. Radius handles its own
    // line grab above (it returns before here).
    const ends = dimensionLineEnds(matrix);
    const onLine = ends && distanceToSegment(event.clientX, event.clientY, ends.start.x, ends.start.y, ends.end.x, ends.end.y) <= GRAB_RADIUS_PX;
    if (onLine && !isRadius()) {
      beginOffsetDrag(event);
      return;
    }

    // Otherwise pan the drawing (the overlay swallows pointer events, so panning
    // is forwarded to pan-zoom).
    dragRef.current = { name: 'pan', lastX: event.clientX, lastY: event.clientY };
    rerender();
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) {
      // Radius: a pointer cursor over a selectable arc/circle signals it is clickable.
      if (isRadius()) {
        const over = !!radiusTargetUnderCursor(event.clientX, event.clientY, geometryMatrix());
        if (over !== radiusHoverRef.current) { radiusHoverRef.current = over; rerender(); }
      }
      return;
    }
    if (drag.name === 'pan') {
      const instance = window.svgPanZoomInstance;
      if (instance && typeof instance.panBy === 'function') {
        instance.panBy({ x: event.clientX - drag.lastX, y: event.clientY - drag.lastY });
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
      }
      rerender();
      return;
    }
    if (drag.name === 'offset') {
      const cursor = clientToUser(event.clientX, event.clientY, geometryMatrix());
      if (!cursor) { return; }
      if (drag.perpendicular) {
        // Angled: how far the cursor moved along the line's perpendicular.
        offsetRef.current = drag.startOffset
          + ((cursor.x - drag.startUserX) * drag.perpendicular.x)
          + ((cursor.y - drag.startUserY) * drag.perpendicular.y);
      } else {
        offsetRef.current = drag.startOffset + (drag.horizontal ? cursor.y - drag.startUserY : cursor.x - drag.startUserX);
      }
      rerender();
      return;
    }
    // Radius: dragging snaps the measurement to the arc/circle nearest the cursor, so a
    // measurement can be dragged straight onto another arc (Infinity = always the nearest).
    if (drag.name === 'radius-move') {
      const matrix = geometryMatrix();
      const ruler = radiusTargetNearestCurve(radiusTargetsRef.current, event.clientX, event.clientY, (point) => userToClient(point, matrix), Infinity);
      if (ruler) { seatRadius(ruler); }
      rerender();
      return;
    }
    const matrix = geometryMatrix();
    const cursor = clientToUser(event.clientX, event.clientY, matrix);
    if (!cursor) { return; }

    // Angled: EITHER handle can be moved. The one being dragged becomes the anchor,
    // snapping to the crease centre under the cursor; the other end recomputes as the
    // 90° perpendicular from that crease to its nearest neighbour. Both ends are
    // therefore freely movable to any crease (you pick the crease with whichever
    // handle you grab) while the ruler stays perpendicular by construction.
    if (isAngled()) {
      const creases = creaseSegments();
      if (creases.length >= 2) {
        // The crease whose CENTRE is nearest the cursor — handles snap to those
        // centres (shown as snap dots), matching "anchor at the line's middle".
        const nearestCreaseTo = (point) => {
          let best = null;
          let bestDistance = Infinity;
          creases.forEach((crease) => {
            const mid = segmentMidpoint(crease);
            const distance = Math.hypot(mid.x - point.x, mid.y - point.y);
            if (distance < bestDistance) { bestDistance = distance; best = { crease, point: mid }; }
          });
          return best;
        };
        const draggedRef = drag.name === 'A' ? handleARef : handleBRef;
        const otherRef = drag.name === 'A' ? handleBRef : handleARef;
        const near = nearestCreaseTo(cursor);
        if (near) {
          draggedRef.current = { x: near.point.x, y: near.point.y, crease: near.crease };
          const across = perpendicularAcross(near.point, near.crease, creases);
          if (across) { otherRef.current = { x: across.b.x, y: across.b.y }; }
        }
      }
      rerender();
      return;
    }

    // Circle/arc-centre snapping. If the dragged handle is over a centre, snap it
    // there; if the OTHER handle is already on a centre, this handle follows the
    // nearest edge point. Every measurement is squared to an axis-aligned line on
    // release (squareUpAcross) — two centres and centre-to-edge alike, never a
    // diagonal — like an edge-to-edge measurement.
    const draggedRef = drag.name === 'A' ? handleARef : handleBRef;
    const otherRef = drag.name === 'A' ? handleBRef : handleARef;
    const centerHit = nearestCenter(snapPointsRef.current, event.clientX, event.clientY, matrix);
    if (centerHit) {
      draggedRef.current = { x: centerHit.x, y: centerHit.y, center: centerHit, placed: true };
      rerender();
      return;
    }
    if (otherRef.current && otherRef.current.center) {
      const nearest = nearestPointOnGeometry(cursor, segmentsRef.current);
      draggedRef.current = nearest ? { x: nearest.point.x, y: nearest.point.y, placed: true } : { x: cursor.x, y: cursor.y, placed: true };
      rerender();
      return;
    }

    // Move only the dragged handle, snapped to the nearest real point; the other
    // handle stays put. The measurement is squared up to a valid axis-aligned line
    // on release (onPointerUp), not on every move — so the two ends can be placed
    // one at a time without the far one flying around.
    const hit = nearestPointOnGeometry(cursor, segmentsRef.current);
    if (!hit) { rerender(); return; }
    draggedRef.current = { x: hit.point.x, y: hit.point.y, placed: true };
    rerender();
  };

  // Pull the edge handle onto the line through the fixed centre handle on the
  // locked axis, so a centre-to-edge measurement is axis-aligned like an
  // edge-to-edge one. Restores the last valid line if the centre has no aligned
  // edge on that side.
  const squareEdgeToCenter = (centerRef, edgeRef, drag) => {
    const center = centerRef.current;
    const axis = isHorizontalRuler() ? 'h' : 'v';
    const aligned = alignedPointOnGeometry(center, axis, segmentsRef.current, edgeRef.current);
    if (aligned) {
      edgeRef.current = { x: aligned.x, y: aligned.y };
    } else {
      handleARef.current = drag.prevA;
      handleBRef.current = drag.prevB;
    }
  };

  // On release, seat the handles so the measurement is a clean axis-aligned line:
  // move the far handle straight across from the one just placed, or, when one end
  // is a snapped centre, pull the edge end onto the line through it. A handle the
  // user deliberately placed (dragged onto its own point) is never relocated — so
  // you can pin BOTH ends on specific points; the render then projects the far one
  // to the intersection corner. Likewise when the far handle can't be aligned (two
  // centres, or no edge straight across — a gap on that side), both points stay put
  // and project. The span is always horizontal or vertical, never a diagonal.
  const squareUpAcross = (drag) => {
    // Angled and Radius measurements aren't axis-aligned, so there is nothing to
    // square up (radius snaps to a whole shape; angled is already perpendicular).
    if (isAngled() || isRadius()) { return; }
    const draggedRef = drag.name === 'A' ? handleARef : handleBRef;
    const otherRef = drag.name === 'A' ? handleBRef : handleARef;
    const dragged = draggedRef.current;
    const other = otherRef.current;
    if (!dragged || !other) { return; }
    if (dragged.center && other.center) { return; }
    if (dragged.center || other.center) {
      const edgeRef = dragged.center ? otherRef : draggedRef;
      // Keep an edge end the user placed themselves; otherwise align it to the centre.
      if (!edgeRef.current.placed) {
        squareEdgeToCenter(dragged.center ? draggedRef : otherRef, edgeRef, drag);
      }
      return;
    }

    // The other end was deliberately placed: keep both and project (don't seat it
    // across, which would drag it off the point the user chose) — UNLESS the two
    // points make the span lie along an existing edge, which is never a valid
    // measurement (it measures a section of one line). Then restore the last valid
    // line, like the other invalid drops do. Centre pairs are handled above, so
    // this only ever guards two placed EDGE points.
    if (other.placed) {
      const axis = isHorizontalRuler() ? 'h' : 'v';
      if (rulerOverlapsEdge(handleARef.current, handleBRef.current, axis, segmentsRef.current)) {
        handleARef.current = drag.prevA;
        handleBRef.current = drag.prevB;
      }
      return;
    }

    const axis = isHorizontalRuler() ? 'h' : 'v';
    const hit = nearestPointOnGeometry(dragged, segmentsRef.current);
    const draggedSegment = hit ? segmentsRef.current[hit.segmentIndex] : null;
    const candidateAxis = allowedAxis(draggedSegment, axis);
    let across = null;
    if (candidateAxis) {
      const candidate = alignedPointOnGeometry(dragged, candidateAxis, segmentsRef.current, otherRef.current);
      if (candidate && !rulerOverlapsEdge(dragged, candidate, candidateAxis, segmentsRef.current)) {
        across = candidate;
      }
    }
    if (across) {
      otherRef.current = across;
    } else if (rulerOverlapsEdge(handleARef.current, handleBRef.current, axis, segmentsRef.current)) {
      // No clean point across on the locked axis and the drop would lie along an
      // edge (e.g. a handle dragged onto an edge running along the locked axis):
      // keep the last valid line rather than an invalid one.
      handleARef.current = drag.prevA;
      handleBRef.current = drag.prevB;
    }
    // Otherwise keep both real points where they are; the projected-corner render
    // makes the non-aligned span a clean axis-aligned line on the locked axis.
  };

  const onPointerUp = () => {
    const drag = dragRef.current;
    if (!drag) { return; }
    dragRef.current = null;
    if (drag.name === 'A' || drag.name === 'B') { squareUpAcross(drag); }
    // Moving a handle, sliding the line, or picking/dragging a radius shape is the user
    // positioning a measurement, so Done should confirm before discarding it (panning
    // doesn't count).
    if (['A', 'B', 'offset', 'radius-move'].includes(drag.name)) { dirtyRef.current = true; }
    rerender();
  };

  // Persist the current ruler as one of the user's own annotations, then ask the
  // drawing to redraw so the server injects it into the render. The handles are
  // in SVG user space (y flipped); the server wants the drawing's own y.
  const addToDrawing = () => {
    const a = handleARef.current;
    const b = handleBRef.current;
    if (!a || !b || !drawingId) { return; }
    // The demo shows the tool to signed-out visitors so they can try it, but saving a
    // measurement needs an account (annotations are per-user). Open the shared sign-in
    // popup (LoginButtonListener, mounted in the layout); the save is finished from the
    // user.session_started event a real sign-in emits (see the listener above), so
    // dismissing the popup saves nothing and a later Save re-opens it. A reload-based
    // sign-in (OAuth, or the non-AJAX path) reloads the page, so there the in-progress
    // measurement is lost rather than saved.
    if (!signedInRef.current) {
      pendingSaveRef.current = true;
      trackEvent('measure_save_sign_up_prompted', {});
      document.dispatchEvent(new CustomEvent('home.log_in_required', {}));
      return;
    }
    // Every measurement is an axis-aligned dimension. Its orientation is the locked
    // type, so send it (rotation 0 = horizontal, 90 = vertical) rather than letting
    // the server infer it from the points — the two endpoints can be at different
    // levels (a horizontal span between points of different height), which the
    // point-based guess would misread as the other axis. Always send the offset
    // (converted to the drawing's own y sign), including 0 when the line sits on
    // the measured points: the ruler is shown at that exact position, so it must
    // render there — not pushed to a default distance away.
    const horizontal = isHorizontalRuler();
    const offset = offsetRef.current;
    const token = document.querySelector('meta[name=csrf-token]')?.getAttribute('content');
    // Editing a saved annotation updates it in place; otherwise create a new one.
    const editingId = editingIdRef.current;
    const url = editingId
      ? `/measure-tool/${drawingId}/annotations/${editingId}`
      : `/measure-tool/${drawingId}/annotations`;
    fetch(url, {
      method: editingId ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({
        measurement_type: measurementTypeRef.current,
        point_a: { x: a.x, y: -a.y },
        // Radius references the SAME circle/arc for both ends; the render draws the
        // line from its centre to a point on its edge, so its length is the radius.
        // The others send the two measured points.
        point_b: isRadius() ? { x: a.x, y: -a.y } : { x: b.x, y: -b.y },
        // The measured radius, so the server can pick the right shape when several
        // are concentric (same centre, different radius).
        radius: isRadius() ? a.radius : undefined,
        rotation: horizontal ? 0 : 90,
        // The perpendicular offset, converted to the drawing's own y sign. Angled,
        // radius and horizontal all flip; vertical keeps its sign.
        offset: (isAngled() || isRadius() || horizontal) ? -offset : offset,
        options: window.currentDrawingOptions || {},
      }),
    }).then((response) => {
      if (response.ok) {
        trackEvent('measure_annotation_saved', {});
        // This drawing now has a saved measurement, so let the list request through
        // even before the redraw below refreshes the flag from the server.
        window.currentDrawingHasMeasureAnnotations = true;
        document.dispatchEvent(new CustomEvent('measure_tool.annotation_changed'));
        // Adding is a commit: close the tool, as clicking Done would.
        manageRef.current = false;
        dirtyRef.current = false;
        setConfirmClose(false);
        setActive(false);
      }
    });
  };

  // Keep the sign-in retry pointed at the current closure (see the session-started
  // listener), so it saves the current geometry and never a stale one.
  addToDrawingRef.current = addToDrawing;

  // Close the tool, dropping any in-progress ruler.
  const closeTool = () => {
    setMenuOpen(false);
    setConfirmClose(false);
    manageRef.current = false;
    dirtyRef.current = false;
    // Done/Discard abandons the measurement, so drop any pending sign-in-then-save too.
    pendingSaveRef.current = false;
    setActive(false);
  };

  // Done: closing would discard an unsaved, positioned measurement, so confirm
  // first. Manage mode has no live ruler to lose, so it closes straight away.
  const requestDone = () => {
    if (!manageRef.current && dirtyRef.current) {
      setMenuOpen(false);
      setConfirmClose(true);
      return;
    }
    closeTool();
  };

  // Faint markers at every snap point so the targets are visible while measuring
  // — the user can drag a handle straight onto one instead of guessing where it
  // sits. Deliberately subtle so the full set can be shown at once without
  // crowding the drawing. Purely visual (no pointer events), positioned like the
  // handles through the geometry's screen CTM.
  const draggingHandle = () => dragRef.current?.name === 'A' || dragRef.current?.name === 'B';

  const renderSnapPointMarkers = () => {
    // Manage mode places no ruler, so the snap targets would just be clutter; Radius
    // shows only the circle/arc centres (renderRadiusTargetMarkers) instead.
    if (manageRef.current || isRadius()) { return null; }
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix || snapPointsRef.current.length === 0) { return null; }
    const overlayRect = overlay.getBoundingClientRect();
    // Brighten and grow the snap points while a handle is being dragged, so they
    // are easy to aim for.
    const dragging = draggingHandle();
    return snapPointsRef.current.map((point, index) => {
      const client = userToClient(point, matrix);
      if (!client) { return null; }
      return (
        <circle
          key={`snap-point-${index}`}
          className={dragging ? styles.snapPointMarkerActive : styles.snapPointMarker}
          cx={client.x - overlayRect.left}
          cy={client.y - overlayRect.top}
          r={dragging ? 4 : 2.5}
        />
      );
    });
  };

  // Radius mode: outline every selectable circle/arc CURVE so it's clear you can
  // click one to measure its radius. The shape currently measured already carries
  // the radial line, so it is skipped here.
  const renderRadiusTargetMarkers = () => {
    if (!isRadius()) { return null; }
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix || radiusTargetsRef.current.length === 0) { return null; }
    const overlayRect = overlay.getBoundingClientRect();
    const active = handleARef.current;
    return radiusTargetsRef.current.map((target, index) => {
      if (active && Math.hypot(active.x - target.x, active.y - target.y) < 0.01) { return null; }
      const points = (target.curve || [])
        .map((sample) => userToClient(sample, matrix))
        .filter(Boolean)
        .map((client) => `${client.x - overlayRect.left},${client.y - overlayRect.top}`);
      if (points.length === 0) { return null; }
      return (
        <polyline key={`radius-target-${index}`} className={styles.radiusCurve} points={points.join(' ')} />
      );
    });
  };

  // A small remove control on each saved annotation. The server gives the
  // midpoint in the drawing's own space; the SVG's y is flipped from it (the same
  // flip the save uses), so the control is placed at { mid_x, -mid_y }.
  const renderAnnotationRemovers = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    if (!overlay || !matrix || annotations.length === 0) { return null; }
    const overlayRect = overlay.getBoundingClientRect();
    return annotations.map((annotation) => {
      const client = userToClient({ x: annotation.mid_x, y: -annotation.mid_y }, matrix);
      if (!client) { return null; }
      const cx = client.x - overlayRect.left;
      const cy = client.y - overlayRect.top;
      return (
        <g key={`controls-${annotation.id}`}>
          <g className={styles.remover} transform={`translate(${cx - 13}, ${cy})`}
            onPointerDown={(event) => { event.stopPropagation(); loadAnnotation(annotation); }}>
            <circle className={styles.editBackground} r="11" />
            <g className={styles.removerMark} transform="translate(-7, -7) scale(0.58)">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" fill="none" />
            </g>
          </g>
          <g className={styles.remover} transform={`translate(${cx + 13}, ${cy})`}
            onPointerDown={(event) => { event.stopPropagation(); removeAnnotation(annotation.id); }}>
            <circle className={styles.removerBackground} r="11" />
            <line className={styles.removerMark} x1="-4" y1="-4" x2="4" y2="4" />
            <line className={styles.removerMark} x1="4" y1="-4" x2="-4" y2="4" />
          </g>
        </g>
      );
    });
  };

  // The value pill and the contextual Save button (a save icon + label), centred
  // at `center` (local overlay coords). When `draggable`, the whole pill is a move
  // target for repositioning the dimension line off the geometry.
  const labelAndCta = (center, valueText, draggable, ctaLift = 0, showCta = true) => {
    const labelWidth = (valueText.length * 8) + 14;
    const labelHeight = 20;
    const saveText = t('measure_tool.add');
    const iconSize = 14;
    const iconGap = 6;
    const addWidth = 12 + iconSize + iconGap + (saveText.length * 8) + 12;
    const addHeight = 24;
    // `ctaLift` raises only the Save button above the value pill so it clears the
    // ruler's end handles (which sit on the line, at the pill's y); the pill stays
    // put.
    const addCenter = { x: center.x + (labelWidth / 2) + 8 + (addWidth / 2), y: center.y - ctaLift };
    const iconX = (-addWidth / 2) + 12 + (iconSize / 2);
    const textX = (-addWidth / 2) + 12 + iconSize + iconGap;
    // The value pill is a drag target — grab it to reposition the measurement: radius
    // moves it to the arc nearest the cursor, the other types slide the line off the
    // geometry. The ruler's handles render on top of it (see below), so they still win a
    // grab where the pill overlaps them on a short ruler.
    const labelProps = draggable
      ? { style: { cursor: 'move' }, onPointerDown: (event) => { event.stopPropagation(); (isRadius() ? beginRadiusMove : beginOffsetDrag)(event); } }
      : {};
    return [
      <g key="label" transform={`translate(${center.x}, ${center.y})`} {...labelProps}>
        <rect className={styles.labelBackground}
          x={-labelWidth / 2} y={-labelHeight / 2} width={labelWidth} height={labelHeight} rx="4" />
        <text className={styles.labelText} x="0" y="0" dominantBaseline="central" textAnchor="middle">{valueText}</text>
      </g>,
      showCta && (
        <g key="add-cta" className={styles.addCta}
          transform={`translate(${addCenter.x}, ${addCenter.y})`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => { event.stopPropagation(); addToDrawing(); }}>
          <rect className={styles.addCtaBackground}
            x={-addWidth / 2} y={-addHeight / 2} width={addWidth} height={addHeight} rx="5" />
          {saveIcon(iconX, iconSize)}
          <text className={styles.addCtaText} x={textX} y="0" dominantBaseline="central" textAnchor="start">{saveText}</text>
        </g>
      ),
    ];
  };

  const renderOverlayContents = () => {
    const overlay = overlayRef.current;
    const matrix = geometryMatrix();
    const a = handleARef.current;
    const b = handleBRef.current;
    if (!overlay || !matrix || !a || !b) { return null; }

    const overlayRect = overlay.getBoundingClientRect();
    const toLocal = (userPoint) => {
      const client = userToClient(userPoint, matrix);
      if (!client) { return null; }
      return { x: client.x - overlayRect.left, y: client.y - overlayRect.top };
    };

    const aLocal = toLocal(a);
    const bLocal = toLocal(b);
    if (!aLocal || !bLocal) { return null; }

    // The dimension line is ALWAYS perfectly horizontal or vertical, whatever the
    // two measured points are — edges (squared up to the same level) or two
    // circle/arc centres at different levels. It sits on one axis (see
    // dimensionLineUserEnds); witness lines connect it back to each measured point
    // when the point is off the line.
    const horizontal = isHorizontalRuler();
    const angled = isAngled();
    const radius = isRadius();
    // Radius/angled are diagonal, so the value is the centre-to-edge / A-to-B
    // distance; H/V take the on-axis span.
    const millimeters = (angled || radius)
      ? Math.hypot(a.x - b.x, a.y - b.y)
      : (horizontal ? Math.abs(a.x - b.x) : Math.abs(a.y - b.y));
    const ends = dimensionLineUserEnds();
    const mainStart = toLocal(ends.start);
    const mainEnd = toLocal(ends.end);
    if (!mainStart || !mainEnd) { return null; }

    // Radius reads as a CAD radial dimension — the value prefixed "R" (e.g. "R 30.00 mm").
    const label = radius ? `R ${formatMeasurement(millimeters, unit)}` : formatMeasurement(millimeters, unit);
    // Angled/Radius: place the value pill just off the line so it doesn't sit on
    // the ruler. H/V keep their fixed offsets.
    let labelCenter;
    if (angled || radius) {
      const midX = (mainStart.x + mainEnd.x) / 2;
      const midY = (mainStart.y + mainEnd.y) / 2;
      const length = Math.hypot(mainEnd.x - mainStart.x, mainEnd.y - mainStart.y) || 1;
      const perpX = -(mainEnd.y - mainStart.y) / length;
      const perpY = (mainEnd.x - mainStart.x) / length;
      labelCenter = { x: midX + (perpX * 16), y: midY + (perpY * 16) };
    } else {
      labelCenter = horizontal
        ? { x: (mainStart.x + mainEnd.x) / 2, y: (mainStart.y + mainEnd.y) / 2 - 14 }
        : { x: (mainStart.x + mainEnd.x) / 2 + 18, y: (mainStart.y + mainEnd.y) / 2 };
    }

    const elements = [
      // Radius shows a single arrowhead at the arc (mainEnd) and nothing at the centre
      // (mainStart); the other types keep the double-headed ruler. The line is a drag
      // target for every type (radius: drag to move the measurement to another arc).
      <line key="ruler" className={styles.rulerLine}
        markerStart={radius ? undefined : 'url(#measureArrow)'} markerEnd="url(#measureArrow)"
        style={{ cursor: 'move' }}
        x1={mainStart.x} y1={mainStart.y} x2={mainEnd.x} y2={mainEnd.y} />,
    ];

    // A witness line from each dimension-line end back toward its measured point,
    // stopping a fixed gap short of it so the extension line doesn't touch the
    // geometry. Skipped when the point is within the gap of the line.
    const addWitness = (end, endUser, point, key) => {
      const stop = witnessEnd(endUser, point);
      if (!stop) { return; }
      const witness = toLocal(stop);
      if (witness) { elements.push(<line key={key} className={styles.witness} x1={end.x} y1={end.y} x2={witness.x} y2={witness.y} />); }
    };
    // Radius has no witness lines — the radial line lies on the geometry, nothing to
    // connect back to.
    if (!radius) {
      addWitness(mainStart, ends.start, a, 'witness-A');
      addWitness(mainEnd, ends.end, b, 'witness-B');
    }

    // The value pill is a drag target for every type: H/V/angled slide the line off the
    // geometry; radius moves the measurement to the arc nearest the cursor.
    elements.push(...labelAndCta(labelCenter, label, true, horizontal ? HANDLE_RADIUS_PX + 8 : 0));
    // Handles render LAST so they sit on top of the value pill and stay grabbable even
    // where the pill overlaps them on a short ruler. Radius has no draggable handles —
    // its centre is not shown, and its shape is picked by clicking the curve.
    if (!radius) {
      elements.push(
        <circle key="handle-A" className={`${styles.handle} ${dragRef.current?.name === 'A' ? styles.handleActive : ''}`}
          cx={aLocal.x} cy={aLocal.y} r={HANDLE_RADIUS_PX} />,
        <circle key="handle-B" className={`${styles.handle} ${dragRef.current?.name === 'B' ? styles.handleActive : ''}`}
          cx={bLocal.x} cy={bLocal.y} r={HANDLE_RADIUS_PX} />,
      );
    }
    return elements;
  };

  const themeClass = theme === 'black_bg' ? styles.dark : styles.light;

  // Reflect the active drag on the whole overlay so the cursor stays right even
  // as the pointer leaves the line: moving the dimension line off the drawing, or
  // panning the canvas.
  let overlayCursor;
  if (dragRef.current?.name === 'pan') { overlayCursor = 'grabbing'; }
  else if (dragRef.current?.name === 'offset') { overlayCursor = 'move'; }
  else if (dragRef.current?.name === 'radius-move') { overlayCursor = 'grabbing'; }
  else if (isRadius() && radiusHoverRef.current) { overlayCursor = 'pointer'; }

  return (
    <>
      <div className={`${styles.toolbar} ${themeClass}`}>
        <div className={styles.group}>
          <div className={styles.dropdown} ref={dropdownRef}>
            <button
              type="button"
              className={`dct-button dct-button--small dct-button--secondary ${styles.toggle}`}
              onClick={() => setMenuOpen((open) => !open)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              {active
                ? axisIcon(measurementType)
                : (
                  <svg className={styles.buttonIcon} width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
                    <rect x="0.75" y="3.5" width="14.5" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
                    <line x1="4" y1="3.5" x2="4" y2="8.5" stroke="currentColor" strokeWidth="1.3" />
                    <line x1="8" y1="3.5" x2="8" y2="7" stroke="currentColor" strokeWidth="1.3" />
                    <line x1="12" y1="3.5" x2="12" y2="8.5" stroke="currentColor" strokeWidth="1.3" />
                  </svg>
                )}
              {active ? t(`measure_tool.${measurementType}`) : t('measure_tool.button')}
              <svg className={styles.caret} width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                <path d="M2 3.5 L5 6.5 L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {menuOpen &&
              <ul className={styles.menu} role="menu" aria-label={t('measure_tool.button')} onKeyDown={onMenuKeyDown}>
                {MEASUREMENT_TYPES
                  .filter((type) => type !== 'radius' || radiusAvailable)
                  .map((type) => (
                    <li key={type} role="none">
                      <button
                        type="button"
                        role="menuitem"
                        aria-current={active && measurementType === type ? 'true' : undefined}
                        className={`${styles.menuItem} ${active && measurementType === type ? styles.menuItemActive : ''}`}
                        onClick={() => selectType(type)}
                      >
                        {axisIcon(type)}
                        {t(`measure_tool.${type}`)}
                      </button>
                    </li>
                  ))}
              </ul>}
          </div>
          {!active && annotations.length > 0 &&
            <button
              type="button"
              className={`dct-button dct-button--small dct-button--secondary ${styles.iconButton}`}
              onClick={enterManageMode}
              title={t('measure_tool.edit_saved')}
              aria-label={t('measure_tool.edit_saved')}
            >
              <svg className={styles.buttonIcon} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>}
        </div>
        {active &&
          <button
            type="button"
            className={`dct-button dct-button--small dct-button--secondary ${styles.toggle}`}
            onClick={requestDone}
          >
            {t('measure_tool.done')}
          </button>}
        {active && (
          <span className={styles.hint}>
            {t(manageRef.current
              ? 'measure_tool.manage_hint'
              : (measurementType === 'radius' ? 'measure_tool.radius_hint' : 'measure_tool.hint'))}
          </span>
        )}
      </div>

      {active &&
        <svg
          ref={overlayRef}
          className={styles.overlay}
          style={{ cursor: overlayCursor }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <defs>
            <marker id="measureArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 1 L 9 5 L 0 9 z" className={styles.arrowHead} />
            </marker>
          </defs>
          <rect className={styles.capture} x="0" y="0" width="100%" height="100%" pointerEvents="all" />
          {renderSnapPointMarkers()}
          {renderRadiusTargetMarkers()}
          {renderOverlayContents()}
          {renderAnnotationRemovers()}
        </svg>}

      {active && confirmClose &&
        <div className={`${styles.confirm} ${themeClass}`} role="dialog" aria-modal="true">
          <div className={styles.confirmCard}>
            <p className={styles.confirmMessage}>{t('measure_tool.confirm_close')}</p>
            <div className={styles.confirmActions}>
              <button type="button" className="dct-button dct-button--small dct-button--primary"
                onClick={() => { setConfirmClose(false); addToDrawing(); }}>
                {t('measure_tool.confirm_save')}
              </button>
              <button type="button" className="dct-button dct-button--small dct-button--danger"
                onClick={closeTool}>
                {t('measure_tool.confirm_discard')}
              </button>
              <button type="button" className="dct-button dct-button--small dct-button--ghost"
                onClick={() => setConfirmClose(false)}>
                {t('measure_tool.confirm_cancel')}
              </button>
            </div>
          </div>
        </div>}
    </>
  );
};

export default MeasureTool;
